import dayjs from "dayjs";
import type {
  ReportBlock,
  ReportFilters,
  BreakdownDimension,
} from "../api/types";

/** Report templates the generator can emit. Mirrors the UU deck sections. */
export type ReportTemplate =
  | "wellbeing_summary"
  | "ai_insights"
  | "weather_impact"
  | "individual_correlation";

export interface GenerateInput {
  templates: ReportTemplate[];
  filters: ReportFilters;
  /** Human title for the cover slide. */
  title: string;
  /** e.g. "June 2026" — shown on the cover and section dividers. */
  periodLabel: string;
}

// Core metrics shown across the summary/breakdown slides — the same set the UU
// deck reports on.
const SUMMARY_METRICS = [
  "sleep_performance",
  "sleep_duration",
  "sleep_debt",
  "recovery_score",
  "strain",
  "calories_burn",
  "resting_heart_rate",
  "hrv",
];

// Comparison endpoint's supported metric ids differ slightly from stats ids
// (e.g. sleep_score vs sleep_performance). Use its vocabulary for breakdowns.
const BREAKDOWN_METRICS = [
  "sleep_score",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];

const BREAKDOWN_DIMENSIONS: { dimension: BreakdownDimension; label: string }[] = [
  { dimension: "gender", label: "Gender" },
  { dimension: "age_group", label: "Age Group" },
  { dimension: "department", label: "Department / Role" },
];

let counter = 0;
const id = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  counter += 1;
  return `gen-${Date.now()}-${counter}`;
};

const pageBreak = (): ReportBlock => ({ id: id(), type: "pageBreak" });

/**
 * Wellbeing Summary: cover → exec RAG summary → gender/age/dept breakdowns.
 * The backbone of the UU report.
 */
const wellbeingSummary = (input: GenerateInput): ReportBlock[] => {
  const blocks: ReportBlock[] = [
    {
      id: id(),
      type: "execSummary",
      title: "Executive Summary",
      metrics: SUMMARY_METRICS,
      filters: input.filters,
    },
  ];

  for (const { dimension, label } of BREAKDOWN_DIMENSIONS) {
    blocks.push(pageBreak());
    blocks.push({
      id: id(),
      type: "breakdown",
      title: `${label} Breakdown`,
      dimension,
      metrics: BREAKDOWN_METRICS,
      filters: input.filters,
    });
  }

  return blocks;
};

/**
 * AI Insights & Action Plan: overall score + highlights/recommendations/
 * areas-to-watch, then an action-plan slide (items filled on demand).
 */
const aiInsights = (input: GenerateInput): ReportBlock[] => [
  {
    id: id(),
    type: "aiPanel",
    title: "AI-Powered Wellness",
    filters: input.filters,
    show: {
      score: true,
      highlights: true,
      recommendations: true,
      areasToWatch: true,
    },
  },
  pageBreak(),
  {
    id: id(),
    type: "actionPlan",
    title: "Recommended Action Plan",
    items: [],
  },
];

/** Weather Impact: wellbeing-vs-weather overlay + anomaly callouts. */
const weatherImpact = (input: GenerateInput): ReportBlock[] => [
  {
    id: id(),
    type: "weatherImpact",
    title: "Weather Impact on Wellbeing",
    metric: "recovery_score",
    filters: input.filters,
  },
];

/**
 * Individual / Correlation: correlation matrix + a sleep-vs-recovery scatter by
 * department. When the report is scoped to a single user, the KPI cards act as
 * the individual deep-dive header.
 */
const individualCorrelation = (input: GenerateInput): ReportBlock[] => {
  const blocks: ReportBlock[] = [];

  if (input.filters.user_ids?.length) {
    blocks.push({
      id: id(),
      type: "kpiCards",
      title: "Individual Overview",
      metrics: BREAKDOWN_METRICS,
      filters: input.filters,
    });
    blocks.push(pageBreak());
  }

  blocks.push({
    id: id(),
    type: "correlation",
    title: "Metric Correlations by Segment",
    metrics: BREAKDOWN_METRICS,
    dimension: "department",
    filters: input.filters,
  });
  blocks.push(pageBreak());
  blocks.push({
    id: id(),
    type: "scatter",
    title: "Sleep vs Recovery by Department",
    xMetric: "sleep_score",
    yMetric: "recovery_score",
    dimension: "department",
    filters: input.filters,
  });

  return blocks;
};

const TEMPLATE_BUILDERS: Record<
  ReportTemplate,
  (input: GenerateInput) => ReportBlock[]
> = {
  wellbeing_summary: wellbeingSummary,
  ai_insights: aiInsights,
  weather_impact: weatherImpact,
  individual_correlation: individualCorrelation,
};

const TEMPLATE_LABELS: Record<ReportTemplate, string> = {
  wellbeing_summary: "Wellbeing Summary",
  ai_insights: "AI Insights & Action Plan",
  weather_impact: "Weather Impact",
  individual_correlation: "Individual & Correlation",
};

/**
 * Build a full slide-block layout from the selected templates. Emits a branded
 * cover, then each template's slides separated by page breaks and section
 * dividers. Resulting blocks are dropped straight into the editor canvas, where
 * they can be reordered/tweaked before export.
 */
export const generateReport = (input: GenerateInput): ReportBlock[] => {
  const blocks: ReportBlock[] = [
    {
      id: id(),
      type: "cover",
      title: input.title,
      subtitle: "Wellbeing Report",
      periodLabel: input.periodLabel,
    },
  ];

  input.templates.forEach((template, index) => {
    blocks.push(pageBreak());
    // Section divider between templates (skip before the first, which follows
    // the cover directly).
    if (index > 0 || input.templates.length > 1) {
      blocks.push({
        id: id(),
        type: "cover",
        title: TEMPLATE_LABELS[template],
        periodLabel: input.periodLabel,
      });
      blocks.push(pageBreak());
    }
    blocks.push(...TEMPLATE_BUILDERS[template](input));
  });

  return blocks;
};

export { TEMPLATE_LABELS };
